import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { selectBrainId } from '@/redux/session/selectors';
import { openSidebar } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { SimulationsList } from './components/List/SimulationsList';
import { SimulationsOverview } from './components/Overview/SimulationsOverview';
import useSchedules from './hooks/useSchedules';
import useSimulations from './hooks/useSimulations';
import {
  SIMULATIONS_LAST_TAB_KEY,
  searchWithoutCreateSimulation,
  takeCreateRequest,
  useCreateSimulationRequest,
} from './navigation';

interface SimulationsPageProps {
  tab: 'overview' | 'simulations';
}

export const SimulationsPage = ({ tab }: SimulationsPageProps) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const createRequest = useCreateSimulationRequest();
  const handledRef = useRef<string | null>(null);

  const {
    simulations,
    listStatus,
    isSimulationsLoading,
    triggerRun,
    deleteSimulation,
    bulkDeleteSimulations,
  } = useSimulations(brainId);
  const { schedules } = useSchedules(brainId);

  useSegments(tab === 'overview' ? PageName.SIMULATIONS_OVERVIEW : PageName.SIMULATIONS);

  useEffect(() => {
    if (!createRequest || tab !== 'simulations' || handledRef.current === createRequest) {
      return;
    }
    handledRef.current = createRequest;
    const { startingContext, transcript } = takeCreateRequest(createRequest);
    localStorage.setItem(SIMULATIONS_LAST_TAB_KEY, 'simulations');
    dispatch(openSidebar({ content: 'create_simulation', meta: { startingContext, transcript } }));
    if (!transcript) {
      toast.info(t('simulations.draft_needs_more_turns'));
    }
    navigate({ pathname, search: searchWithoutCreateSimulation(search), hash }, { replace: true });
  }, [createRequest, tab, dispatch, navigate, pathname, search, hash, t]);

  const handleRun = useCallback(
    (ids: string[]) => {
      trackEvent(EventName.RunSimulations, { run_type: 'selected' });
      triggerRun({ simulation_ids: ids });
    },
    [triggerRun]
  );

  const handleRunAll = useCallback(() => {
    trackEvent(EventName.RunSimulations, { run_type: 'all' });
    triggerRun(undefined);
  }, [triggerRun]);

  return (
    <div className={TABLE_LAYOUT_FILL_CLASSES}>
      {tab === 'overview' ? (
        <SimulationsOverview brainId={brainId} />
      ) : (
        <SimulationsList
          brainId={brainId}
          simulations={simulations}
          schedules={schedules}
          isLoading={isSimulationsLoading}
          isError={listStatus === 'error'}
          onDelete={deleteSimulation}
          onBulkDelete={bulkDeleteSimulations}
          onRun={handleRun}
          onRunAll={handleRunAll}
        />
      )}
    </div>
  );
};
